/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DirectDialPage.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Main page for Direct Dial management - simplified using hooks and API modules.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Edit, Copy, Trash2 } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { deleteDirectDialById, navigateToDirectDialDuplicate } from './directDialActions';
import { useDirectDialList } from './hooks/useDirectDialList';
import type { DirectDial } from './types';

export default function DirectDialPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; application: DirectDial | null }>({
    isOpen: false,
    application: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Generate columns
  const availableColumns = useMemo(() => {
    return [
      {
        key: 'code',
        label: t('directDial.code'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: DirectDial) => (
          <span className="font-mono font-medium text-primary">{item?.code || '-'}</span>
        )
      },
      {
        key: 'name',
        label: t('directDial.name'),
        visible: true,
        sortable: true
      },
      {
        key: 'destination',
        label: t('directDial.destination'),
        visible: true,
        sortable: false,
        render: (_value: unknown, item: DirectDial) => {
          if (!item) return '-';
          return (
            <span className="text-sm">
              <span className="font-medium">{item.destinationModule}</span>
              {' / '}
              <span className="text-muted-foreground">{item.destinationValue}</span>
            </span>
          );
        }
      },

      {
        key: 'enabled',
        label: t('directDial.enabled'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: DirectDial) => (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            }`}>
            {item?.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        )
      },
      {
        key: 'actions',
        label: t('common.actions'),
        visible: true,
        sortable: false
      }
    ];
  }, [t]);

  // Use custom hook for list management
  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDirectDialList({
    initialColumns: availableColumns,
    initialSortBy: 'code',
    initialSortOrder: 'asc',
    initialLimit: 10,
    domainId: selectedDomain?.id,
  });

  // Merge columns with custom renderers
  const enhancedColumns = useMemo(() => {
    const baseColumns = columns.length > 0 ? columns : availableColumns;
    return baseColumns.map((col) => {
      if (col.key === 'actions') {
        return {
          ...col,
          render: (_value: unknown, item: DirectDial) => renderRowActions(item),
        };
      }
      return col;
    });
  }, [columns, availableColumns]);

  // Handlers
  const handleCreate = useCallback(() => {
    navigate('/pbx/applications/direct-dial/new');
  }, [navigate]);

  const handleEdit = useCallback((application: DirectDial) => {
    navigate(`/pbx/applications/direct-dial/${application.uuid || application.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((application: DirectDial) => {
    navigateToDirectDialDuplicate(navigate, String(application.uuid || application.id));
  }, [navigate]);

  const handleDeleteClick = useCallback((application: DirectDial) => {
    setDeleteModal({ isOpen: true, application });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.application) return;

    setIsDeleting(true);
    try {
      await deleteDirectDialById(String(deleteModal.application.uuid || deleteModal.application.id));
      showSuccess(t('directDial.messages.deletedSuccess'));
      setDeleteModal({ isOpen: false, application: null });
      await refresh();
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('directDial.messages.deleteError');
      showError(msg);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal, refresh, showSuccess, showError, t]);

  // Render row actions
  const renderRowActions = useCallback((application: DirectDial) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => handleEdit(application)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(application)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => handleDeleteClick(application)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleDeleteClick, handleDuplicate, handleEdit, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            columns={enhancedColumns}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onAdd={handleCreate}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onDelete={handleDeleteClick}
            onColumnToggle={handleColumnToggle}
            title={t('directDial.title')}
            titleIcon={<Phone className="h-5 w-5 text-primary" />}
            addButtonText={t('common.add')}
            emptyMessage={t('directDial.emptyMessage')}
          />

          <ConfirmationModal
            isOpen={deleteModal.isOpen}
            onClose={() => setDeleteModal({ isOpen: false, application: null })}
            onConfirm={handleDeleteConfirm}
            title={t('directDial.deleteTitle')}
            message={t('directDial.messages.confirmDelete', {
              name: deleteModal.application?.name || ''
            })}
            confirmText={t('common.delete')}
            cancelText={t('common.cancel')}
            type="danger"
            isLoading={isDeleting}
          />
        </>
      }
      formView={null}
    />
  );
}
